<template>
  <div class="login-page">
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">LOGIN</h1>
      </div>
    </div>

    <!-- Login Form -->
    <div class="viva-container page-content">
      
      <div class="login-form-wrapper">
        <div class="viva-section login-form-container">
          <div class="login-header">
            <img src="/viva/viva_logo.svg" alt="Viva Logo" class="login-form-logo">
          </div>
          <p class="login-subtitle">Enter your credentials to continue</p>

          <!-- Session error message -->
          <div v-if="hasSessionError" class="session-error-banner">
            <div class="banner-icon">
              <i class="fa fa-exclamation-triangle"></i>
            </div>
            <div class="banner-content">
              <h4>Session Error</h4>
              <p>There was a problem with your session. Please log in again to continue.</p>
            </div>
          </div>

          <!-- First-time student setup message -->
          <div v-if="isStudentAssignmentRedirect" class="first-time-setup-banner">
            <div class="banner-icon">
              <i class="fa fa-info-circle"></i>
            </div>
            <div class="banner-content">
              <h4>First time here?</h4>
              <p>If your instructor added you to an assignment but you haven't set up your account yet, click <strong>"Reset it"</strong> below to create your password. Make sure to use the email address your instructor used when adding you.</p>
            </div>
          </div>

          <!-- Email Verification State -->
          <div v-if="emailVerificationState.show" class="email-verification-container">
            <div class="verification-icon">
              <i class="fa fa-envelope" aria-hidden="true"></i>
            </div>
            <h3>Email Verification Required</h3>
            <p class="verification-message">{{ emailVerificationState.message }}</p>

            <form @submit.prevent="submitLoginCode">
              <div class="form-group">
                <input
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  v-model="logincode"
                  placeholder="123456"
                  class="form-input"
                >
              </div>

              <div v-if="errors.general" class="viva-alert viva-alert-danger">
                {{ errors.general }}
              </div>

              <div class="verification-actions">
                <button
                  type="submit"
                  class="viva-btn viva-btn-green"
                  :disabled="loading"
                >
                  <span v-if="loading">Verifying...</span>
                  <span v-else>Verify</span>
                </button>
                <button
                  type="button"
                  @click="cancelVerification"
                  class="viva-btn viva-btn-outline"
                >
                  Cancel
                </button>
              </div>
            </form>

            <p class="text-link" style="margin-top: 12px">
              Didn't get it?
              <span class="link-accent" @click="requestNewCode">Send a new code</span>
            </p>
          </div>

          <!-- Login Form -->
          <form v-else @submit.prevent="handleLogin">
            <div class="form-group">
              <input
                type="email"
                v-model="loginForm.email"
                placeholder="Email"
                class="form-input"
                :class="{ 'error': errors.email }"
              >
              <span class="required-asterisk">*</span>
            </div>
            <div v-if="errors.email" class="error-message">{{ errors.email }}</div>

            <div class="form-group">
              <input
                type="password"
                v-model="loginForm.password"
                placeholder="Password"
                class="form-input"
                :class="{ 'error': errors.password }"
              >
              <span class="required-asterisk">*</span>
            </div>
            <div v-if="errors.password" class="error-message">{{ errors.password }}</div>

            <div v-if="errors.general" class="viva-alert viva-alert-danger">
              {{ errors.general }}
            </div>

            <div class="form-actions">
              <button
                type="submit"
                class="viva-btn viva-btn-green login-btn"
                :disabled="!isFormValid || loading"
              >
                <span v-if="loading">Logging in...</span>
                <span v-else>Login <i class="fa fa-arrow-right"></i></span>
              </button>
            </div>
          </form>
          
          <div class="form-footer">
            <p class="text-link">
              Forgot your password? <span class="link-accent" @click="goToReset">Reset it</span>
            </p>
            <p class="text-link">
              Don't have an account?
              <router-link class="link-accent" to="/register">Create one</router-link>
              as a college applicant or join the
              <a
                class="link-accent"
                target="_blank"
                rel="noopener noreferrer"
                href="https://initialview.typeform.com/to/wJ1JVuVs"
              >waitlist</a>
              as a teacher/administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="back-section">
      <p class="back-link" @click="goBack">
        <i class="fa fa-arrow-left"></i> Back
      </p>
    </div>

    <Footer />
  </div>
</template>

<script>
import apiClient from '@/utils/apiClient.js'
import Footer from '@/components/Footer.vue'
import env from '@/utils/env.js'

export default {
  name: 'Login',
  components: {
    Footer
  },
  data() {
    return {
      env: env,
      loginForm: {
        email: '',
        password: ''
      },
      errors: {
        email: '',
        password: '',
        general: ''
      },
      loading: false,
      emailVerificationState: {
        show: false,
        message: '',
        isPolling: false
      },
      pollingInterval: null,
      deviceToken: null,
      logincode: ''
    }
  },
  computed: {
    isFormValid() {
      return (
        this.loginForm.email.trim() &&
        this.loginForm.password.trim() &&
        this.isValidEmail(this.loginForm.email)
      );
    },

    isStudentAssignmentRedirect() {
      const redirect = this.$route.query.redirect;
      if (!redirect) return false;
      // Check if redirect is to a coursework student portal page (shared assignment link)
      // Specifically match coursework routes, not admissions portal (/student/portal)
      const courseworkRoutes = ['/student/dashboard', '/student/course', '/student/assignment'];
      return courseworkRoutes.some(route => redirect.startsWith(route));
    },

    hasSessionError() {
      return this.$route.query.error === 'session_error';
    }
  },

  created() {
    // Restore any existing verification state from localStorage (following Marko pattern)
    this.deviceToken = localStorage.getItem('device_token');
  },

  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    
    validateForm() {
      this.errors = { email: '', password: '', general: '' };
      
      if (!this.loginForm.email.trim()) {
        this.errors.email = 'Email is required';
        return false;
      }
      
      if (!this.isValidEmail(this.loginForm.email)) {
        this.errors.email = 'Please enter a valid email address';
        return false;
      }
      
      if (!this.loginForm.password.trim()) {
        this.errors.password = 'Password is required';
        return false;
      }
      
      return true;
    },
    
    async handleLogin() {
      // Entry point. Also the submit handler, which passes an event we ignore.
      return this.attemptLogin(false);
    },

    async attemptLogin(isRetry) {
      if (!this.validateForm()) return;

      this.loading = true;
      this.errors.general = '';

      try {
        // Generate/manage login_session using localStorage (following Marko pattern)
        if (!localStorage.getItem('login_session') && !localStorage.getItem('device_token')) {
          localStorage.setItem('login_session', this.generateLoginSession());
        }

        // Send EITHER login_session OR device_token, but not both (following Marko pattern)
        const loginData = {
          username: this.loginForm.email,
          password: this.loginForm.password,
        };

        // Add only one authentication parameter at a time (following Marko pattern)
        if (this.deviceToken || localStorage.getItem('device_token')) {
          loginData.device_token = this.deviceToken || localStorage.getItem('device_token');
        } else {
          loginData.login_session = localStorage.getItem('login_session');
        }

        const response = await apiClient.post('/api/user/api-token-auth/', loginData);

        if (response.data.status === 200) {
          if (response.data.token) {
            // SUCCESS: Regular user - store token and redirect
            sessionStorage.setItem('apitoken', response.data.token);

            // Set authorization header for future requests
            apiClient.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;

            // Clear vparam when logging in (switching from coursework to authenticated mode)
            sessionStorage.removeItem('vparam');
            sessionStorage.removeItem('upload_token');

            // Clean up any verification state
            // this.resetAuthenticationState();

            // Redirect to intended page or default to upload
            // Router.js will handle the final routing based on user type
            const redirectPath = this.$route.query.redirect || '/upload';
            this.$router.push(redirectPath);

          } else if (response.data.device_token) {
            // EMAIL VERIFICATION: Staff/admin/QA user
            this.deviceToken = response.data.device_token;
            localStorage.setItem('device_token', response.data.device_token);
            localStorage.removeItem('login_session'); // Replace login_session with device_token

            this.showEmailVerification();

          } else if (response.data.poll) {
            // A verification is already pending for this device - ask for the code
            this.showEmailVerification();
          }
        } else if (response.data.status === 99) {
          // The stored session/device token is stale - the device trust expired,
          // or the row was cleared server-side. Start a clean login once rather
          // than making the user press Login again.
          this.resetAuthenticationState();
          if (!isRetry) {
            return this.attemptLogin(true);
          }
          this.errors.general = 'Session expired. Please try again.';
        } else {
          this.errors.general = 'Login failed. Please try again.';
        }

      } catch (error) {
        console.error('Login error:', error);

        if (error.response) {
          if (error.response.status === 400) {
            this.errors.general = 'Invalid email or password';
          } else if (error.response.status === 403) {
            this.errors.general = 'Account access denied';
          } else {
            this.errors.general = 'Login failed. Please try again.';
          }
        } else {
          this.errors.general = 'Network error. Please check your connection.';
        }
      } finally {
        this.loading = false;
      }
    },

    generateLoginSession() {
      // Generate random session ID (following existing pattern)
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 30; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    showEmailVerification() {
      this.emailVerificationState = {
        show: true,
        message: 'We emailed you a verification code. Enter it below to finish signing in.',
        isPolling: false
      };
    },

    async submitLoginCode() {
      if (!this.logincode) {
        this.errors.general = 'Please enter the code from your email.';
        return;
      }
      this.errors.general = '';
      this.loading = true;

      try {
        const response = await apiClient.post('/api/user/verify_login_code/', {
          device_token: this.deviceToken || localStorage.getItem('device_token'),
          code: this.logincode
        });

        this.loading = false;
        if (response.data.status === 200) {
          // Code accepted: the device is activated, so logging in returns a token.
          this.handleLogin();
        } else if (response.data.code_locked) {
          // the fifth wrong guess cancels the code server-side (card 652).
          this.errors.general = 'Too many incorrect codes, so that code has been cancelled. Send a new code to try again.';
        } else {
          this.errors.general = "That code didn't work. Check it and try again, or send a new one.";
        }
      } catch (error) {
        this.loading = false;
        this.errors.general = "That code didn't work. Check it and try again.";
      }
    },

    requestNewCode() {
      // Re-run the login with a fresh session so the server issues a new code.
      this.logincode = '';
      this.errors.general = '';
      localStorage.removeItem('device_token');
      localStorage.removeItem('login_session');
      this.deviceToken = null;
      this.emailVerificationState.show = false;
      this.handleLogin();
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.emailVerificationState.isPolling = false;
    },

    resetAuthenticationState() {
      localStorage.removeItem('login_session');
      localStorage.removeItem('device_token');
      this.deviceToken = null;
      this.stopPolling();
      this.emailVerificationState.show = false;
    },

    cancelVerification() {
      this.resetAuthenticationState();
      this.errors.general = '';
    },

    goToReset() {
      window.open(`${env.serverAddr}/password/forgot`, '_blank', 'noopener,noreferrer');
    },
    
    goBack() {
      this.$router.push('/');
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

/* First-time student setup banner */
.first-time-setup-banner {
  background: rgba(236, 142, 0, 0.1);
  border: 2px solid var(--viva-orange);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.first-time-setup-banner .banner-icon {
  font-size: 28px;
  color: var(--viva-orange);
  flex-shrink: 0;
}

.first-time-setup-banner .banner-content h4 {
  color: var(--viva-dark);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.first-time-setup-banner .banner-content p {
  color: var(--viva-grey);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.first-time-setup-banner .banner-content strong {
  color: var(--viva-orange);
}

/* Session error banner */
.session-error-banner {
  background: rgba(220, 53, 69, 0.1);
  border: 2px solid #dc3545;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.session-error-banner .banner-icon {
  font-size: 28px;
  color: #dc3545;
  flex-shrink: 0;
}

.session-error-banner .banner-content h4 {
  color: var(--viva-dark);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.session-error-banner .banner-content p {
  color: var(--viva-grey);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.login-page {
  background-color: var(--viva-cream);
  min-height: 100vh;
  padding: 0;
}

/* Header section matching Register.vue */
.header-section {
  display: flex;
  border: 2px solid black;
  background-color: var(--viva-cream);
  height: 80px;
}

.logo-section {
  flex: 1;
  border-right: 2px solid black;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  background-color: var(--viva-cream);
}

.empty-section {
  flex: 2;
  background-color: var(--viva-cream);
}

.login-form-logo {
  height: 60px;
  width: auto;
  padding: 10px 5px;
}

/* Login-specific styles */
.login-form-wrapper {
  max-width: 600px;
  width: 100%;
}

.login-form-container {
  width: 100%;
  margin: 0;
}

.login-header {
  font-size: 1.5em;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.login-subtitle {
  color: var(--viva-grey);
  font-size: 20px;
  margin: 0 0 30px 0;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
}

/* Form styles now handled by viva-classes.css */

.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
}

/* Form and text link styles now handled by viva-classes.css */

/* Email Verification Styles */
.email-verification-container {
  text-align: center;
  padding: 40px 20px;
}

.verification-icon {
  font-size: 48px;
  color: var(--viva-green);
  margin-bottom: 20px;
}

.email-verification-container h3 {
  color: #333;
  margin-bottom: 15px;
  font-size: 24px;
  font-weight: 600;
}

.verification-message {
  color: var(--viva-grey);
  font-size: 16px;
  margin-bottom: 25px;
  line-height: 1.5;
}

.polling-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 30px;
  font-size: 14px;
  color: var(--viva-grey);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--viva-green);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.verification-actions {
  margin-top: 25px;
}

.viva-btn-outline {
  background-color: transparent;
  border: 2px solid var(--viva-green);
  color: var(--viva-green);
}

.viva-btn-outline:hover {
  background-color: var(--viva-green);
  color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-form-wrapper {
    margin: 20px;
  }

  .login-form-container {
    padding: 30px 20px;
  }

  .email-verification-container {
    padding: 30px 15px;
  }

  .verification-icon {
    font-size: 40px;
  }

  .email-verification-container h3 {
    font-size: 20px;
  }
}
</style>